import { ApolloError, DocumentNode, useMutation } from '@apollo/client';
import styled, {
  Checkbox,
  falconTheme,
  IconName,
  IFalconTheme,
  StyledComponent,
  Tooltip,
  TooltipPosition,
  withTheme,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { v4 } from 'uuid';
import { TranslationsContext, UserContext } from '../../common/contexts/index';
import { IFavoriteResponse } from '../../common/types/apiSchema';
import { Color } from '../../common/types/color';
import { ErrorKey, ErrorType } from '../../common/utils/tracking/tracking.interfaces';
import { trackError } from '../../common/utils/tracking/trackingUtils';
import { ADD_FAVORITE, REMOVE_FAVORITE } from './HeartIcon.queries';

interface IHeartIconProps {
  theme?: IFalconTheme;
  checked?: boolean;
  saleId: string;
  baseUrl?: string;
}

interface IHeartIconState {
  checked: boolean;
  iconColor: string;
  isProcessing: boolean;
  tooltipTriggerKey: string;
}

interface IMutationProps {
  mutation: DocumentNode;
  onCompleted: (data: any) => void;
  onError: (error: ApolloError) => void;
}

const HeartIconCheckbox: StyledComponent<typeof Checkbox, IFalconTheme, {}> = styled(Checkbox)`
  outline: none
  &:hover {
    box-shadow: none;
  }
`;

/**
 * Get the fill colour depending on the state of the icon
 * @param checked the current state of the icon
 */
function getIconColor(checked: boolean): Color {
  return checked ? Color.red : Color.grey;
}

/**
 * This is an icon checkbox with the heart icon svg. It passes an onChange handler to the checkbox
 * which sends a post request to the favourites API when checked and a delete request to the
 * favourites api when it is unchecked. The icon is red when checked and grey when unchecked
 * This has a tooltip which displays text based on a translation key and a success popup which takes
 * another translation key.
 */
const BaseHeartIcon: React.FunctionComponent<IHeartIconProps> = props => {
  /**
   * Contains the following state variables
   * checked: whether the checkbox is checked
   * icon: the color of the icon
   * statusKey: the translation key for the status notification
   * tooltipTriggerKey: A uuid used as the key for the tooltip trigger
   * The tooltip trigger could potentially be an array of elements and therefore requires a key.
   */
  const [state, setState] = React.useState<IHeartIconState>({
    checked: props.checked || false,
    iconColor: getIconColor(props.checked),
    isProcessing: false,
    tooltipTriggerKey: v4(),
  });

  const [statusPopupVisible, setStatusPopupVisible] = React.useState<boolean>(false);

  React.useEffect(() => {
    setState({
      ...state,
      checked: props.checked || false,
      iconColor: getIconColor(props.checked),
    });
  }, [props.checked]);

  const { userId } = React.useContext(UserContext);
  const { translations } = React.useContext(TranslationsContext);

  if (!userId) {
    // No user is logged in
    return null;
  }

  /**
   * Shows the notification and calls the generic mutation completed function with the json from the response.
   * This is necessary because GraphQL responses contain the query name by default, but we only want the actual data.
   */
  const onAddFavoriteCompleted: (data: any) => void = data => {
    onMutationCompleted(data.addFavorite);
  };

  /**
   * Calls the generic mutation completed function with the json from the response.
   * This is necessary because GraphQL responses contain the query name by default, but we only want the actual data.
   */
  const onRemoveFavoriteCompleted: (data: any) => void = data => {
    onMutationCompleted(data.removeFavorite);
  };

  /**
   * Checks if the mutation was a success and sets the new state.
   */
  const onMutationCompleted: (data: IFavoriteResponse) => void = React.useCallback(
    data => {
      let newState: IHeartIconState = { ...state };
      if (data.success) {
        const newChecked: boolean = !state.checked;
        const newIconColor: Color = getIconColor(newChecked);

        newState = { ...state, checked: newChecked, iconColor: newIconColor };
      }
      setState({ ...newState, isProcessing: false });
    },
    [state.checked, state.iconColor]
  );

  /**
   * This is for when an error happened with the mutation. For now, we are just failing silently.
   */
  const onMutationError: (error: ApolloError) => void = error => {
    // Failing silently. Error will be tracked, but not displayed to the user.
    trackError(ErrorKey.FAVOURITE_UPDATE, ErrorType.MUTATION_ERROR, error.message, error);

    setState({ ...state, isProcessing: false });
  };

  /**
   * Object containing the mutation properties to add favorite
   */
  const addFavoriteMutationProps: IMutationProps = {
    mutation: ADD_FAVORITE,
    onCompleted: onAddFavoriteCompleted,
    onError: onMutationError,
  };

  /**
   * Object containing the mutation properties to remove favorite
   */
  const removeFavoriteMutationProps: IMutationProps = {
    mutation: REMOVE_FAVORITE,
    onCompleted: onRemoveFavoriteCompleted,
    onError: onMutationError,
  };

  React.useEffect(() => {
    const timeout: number = 2000;

    if (statusPopupVisible) {
      setTimeout(setPopupInvisible, timeout);
    }
  }, [statusPopupVisible]);

  /**
   * Callback for the timout which sets the popup to be invisible
   */
  const setPopupInvisible: () => void = () => {
    setStatusPopupVisible(false);
  };

  const { theme } = props;
  const { checked, iconColor } = state;

  const [addFavorite] = useMutation(addFavoriteMutationProps.mutation, addFavoriteMutationProps);
  const [removeFavorite] = useMutation(
    removeFavoriteMutationProps.mutation,
    removeFavoriteMutationProps
  );

  /**
   * This receives the mutation function created by the Mutation component and invokes it with the right values.
   */
  const handleChange: () => void = React.useCallback(() => {
    if (state.isProcessing === false) {
      const { saleId } = props;

      setState({ ...state, isProcessing: true });

      if (state.checked) {
        removeFavorite({
          variables: { saleId, userId },
        });
      } else {
        addFavorite({
          variables: { saleId, userId },
        });
      }
    }
  }, [state.isProcessing, state.checked]);

  const translation: string = i18n('wishList.addTo.tooltip', [], translations);

  return (
    <Tooltip content={translation} disableTouch={true} position={TooltipPosition.bottomLeft}>
      <div>
        <div key={state.tooltipTriggerKey} data-trackingid="heartIcon">
          <HeartIconCheckbox
            checked={checked}
            icon={IconName.heart}
            onChange={handleChange}
            iconFillColor={iconColor}
            iconStrokeColor={theme.color.white}
            iconStrokeWidth="1px"
          />
        </div>
      </div>
    </Tooltip>
  );
};

BaseHeartIcon.displayName = 'HeartIcon';

/**
 * Sets checked to false by default. The majority of heart icons are unchecked with a relatively
 * small number being checked.
 * If no theme is passed then the secret escapes falcon theme is used.
 */
BaseHeartIcon.defaultProps = {
  checked: false,
  theme: falconTheme,
};

export default withTheme(BaseHeartIcon);

export { BaseHeartIcon, IHeartIconProps, IHeartIconState };
